import { kebabCase, noop } from 'lodash/fp';
import { ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';

import ActionsNode from 'dashboard/apps/InquiryTemplateEditor/ActionsNode';
import CompleteStepNode from 'dashboard/apps/InquiryTemplateEditor/CompleteStepNode';
import ConditionalNode from 'dashboard/apps/InquiryTemplateEditor/ConditionalNode';
import ConditionalRootNode from 'dashboard/apps/InquiryTemplateEditor/ConditionalRootNode';
import useEnhancedLimitingForAdditional from 'dashboard/apps/InquiryTemplateEditor/hooks/useEnhancedLimitingForAdditional';
import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import useHasITEWritePermissionsForObjects from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasITEWritePermissionsForObjects';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import { getScreenDimensionsLiveUpdated } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/selectors/screenDimensions';
import {
  CombinedStepForTree,
  ScreenDimensions,
  TemplateStepPreview,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { validationErrorsForWorkflowNode } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import {
  AddStepConfig,
  DeleteStepConfig,
  ScreenFlowStepType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import {
  getConditionalStepForToken,
  getNodeWrapperAttributes,
  isScreenStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import InquiryTemplateScreenPreview from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview';
import {
  BranchNode,
  useInquiryTemplateTreeContent,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/InquiryTemplateTreeProvider';
import {
  calculateScreenFlowPadding,
  ConditionalWrapper,
  workflowErrorRouteDescription,
  workflowErrorRouteLabel,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/shared';
import ScreenFlow from 'dashboard/apps/InquiryTemplateEditor/ScreenFlow';
import { PathHighlightInformation } from 'dashboard/apps/InquiryTemplateEditor/ScreenFlow/ScreenFlowElements';
import ScreenFlowAddStepButton from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowAddStepButton';
import ScreenFlowNodeWrapper from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper';
import ExpandableScreenFlowNodeWrapper from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper/ExpandableScreenFlowNodeWrapper';
import ScreenTransitionNode from 'dashboard/apps/InquiryTemplateEditor/ScreenTransitionNode';
import { uiStepAllComponents } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import WrappedConnectorNode from 'dashboard/apps/InquiryTemplateEditor/WrappedConnectorNode';
import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { InquiryStatus } from 'dashboard/models/inquiry';
import { Feature, InquiryTemplateVersionStep } from 'dashboard/models/inquiry-template-version';
import { ThemeAttributes } from 'dashboard/models/theme';
import { WorkflowConfigError } from 'dashboard/models/workflow';
import { WorkflowStepType } from 'dashboard/models/workflow-version';
import { RootState } from 'dashboard/redux/store';
import { StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import { isTextComponent, isUiTransitionComponent } from 'lib/interfaces/ui-components';

const getScreenRefId = ({
  step,
  previousStep,
  previousStepBranchIndex,
  parentWorkflowNodeToken,
}: {
  step: CombinedStepForTree;
  previousStep: CombinedStepForTree | undefined;
  previousStepBranchIndex?: number;
  parentWorkflowNodeToken: string | undefined;
}): string => {
  if (!step.isConnector) {
    return step.token;
  }
  // avoids ref ID collisions based on connector position
  return [previousStep?.token, parentWorkflowNodeToken, previousStepBranchIndex, step.token]
    .filter((token) => token != null)
    .join('.');
};

type WorkflowValidationConfig = {
  versions: WorkflowVersionMap;
  errors: Record<string, WorkflowConfigError[]>;
};

interface InquiryTemplateTreeProps {
  basePath: string;
  tree: CombinedStepForTree[];
  steps: InquiryTemplateVersionStep[];
  theme: ThemeAttributes;
  workflowValidationConfig: WorkflowValidationConfig;
  activeNodeToken: string | undefined;
  clipboardHasStep: boolean;
  editableForBasicTier: boolean;
  editableForFreeTier: boolean;
  selectNode: (token: string) => void;
  selectConditionalRouteToEdit: (routeIndex: number) => void;
  handleCopyStep: (step: TemplateStepPreview) => void;
  handleStepTypeSelected: (stepType: ScreenFlowStepType, addStepConfig: AddStepConfig) => void;
  handleDeleteStep: (deleteStepConfig: DeleteStepConfig) => void;
  handleNavToWorkflow: (
    workflowVersionId: string,
    workflowStepToken: string,
    routeIndex?: number,
  ) => void;
  handleNavToScreen: (stepName: string, pageKey?: string) => void;
}

/**
 * This component is responsible for rendering an InquiryTemplateVersion tree
 * within the ITE flow editor which displays all of the screens and potential paths.
 *
 * Note: There is also an InquiryRunTree which is intended to render an identical tree
 * given the same InquiryTemplateVersion with different visual treatment to display the
 * path that a user *did* take through the Inquiry flow.
 *
 * When making changes to this component, please check if InquiryRunTree
 * needs to be updated for parity, and vice versa
 */
const InquiryTemplateTree = ({
  steps,
  basePath,
  tree,
  theme,
  workflowValidationConfig,
  activeNodeToken,
  clipboardHasStep,
  editableForBasicTier,
  editableForFreeTier,
  selectNode,
  selectConditionalRouteToEdit,
  handleCopyStep,
  handleStepTypeSelected,
  handleDeleteStep,
  handleNavToWorkflow,
  handleNavToScreen,
}: InquiryTemplateTreeProps) => {
  const { errors: workflowValidationErrors, versions: workflowVersions } = workflowValidationConfig;
  const { zoomableRef } = usePanZoomBoxControls();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { screenAndNodeRefs, saveRef } = useInquiryTemplateTreeContent();
  const isITEInternal = useIsITEInternal();
  const hasVerificationWritePermission = useHasITEWritePermissionsForObjects([
    'verification-template',
  ]);
  const hasAccessForBasicTier = useHasAccessForTierDeterminator()(EditorAccessTier.InquiriesBasic);

  const enableCopyPaste = handleCopyStep != null;
  const {
    [Feature.Step]: limitAddStep,
    [Feature.Action]: limitAddAction,
    [Feature.Conditional]: limitAddConditional,
  } = useEnhancedLimitingForAdditional();

  const refMap = useMemo<Map<CombinedStepForTree, string>>(
    () =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      screenAndNodeRefs.reduce((map, x) => {
        map.set(x.step, x.id);
        return map;
      }, new Map()),
    [screenAndNodeRefs],
  );
  const screenDimensions = useSelector<RootState, ScreenDimensions>((state: RootState) =>
    getScreenDimensionsLiveUpdated(state),
  );

  const screenHeightWithPadding =
    screenDimensions.height + calculateScreenFlowPadding(screenDimensions.height);

  const renderCard = useCallback(
    (
      step: CombinedStepForTree,
      previousStep?: CombinedStepForTree,
      previousStepBranchIndex?: number,
      nextStep?: CombinedStepForTree,
      parentWorkflowNodeToken?: string,
    ) => {
      const { dataMarketingId, dataName, dataTest, title } = getNodeWrapperAttributes(step);
      const isActiveNode = activeNodeToken === step.token;
      // don't allow initial steps to be deleted or cut
      const deleteEnabled = previousStep != null && editableForBasicTier;

      let node: ReactNode = null;
      let expandNodes: ReactElement[] = [];
      let hoverEditLink = null;
      let stepStatuses: InquiryStatus[] = [];
      const uniqueId =
        refMap.get(step) ??
        getScreenRefId({ step, previousStep, previousStepBranchIndex, parentWorkflowNodeToken });

      if (step.type === 'workflow') {
        const workflowErrors = validationErrorsForWorkflowNode(
          step,
          workflowVersions[step.workflowVersionId].attributes['steps-config'],
          workflowValidationErrors[step.workflowVersionId] ?? [],
        );
        switch (step.workflowStep.type) {
          case WorkflowStepType.Action:
          case WorkflowStepType.Placeholder:
          case WorkflowStepType.Parallel:
          case WorkflowStepType.Wait: {
            hoverEditLink = `${basePath}/actions/${step.workflowVersionId}/${step.workflowStep.token}`;
            let actionsNodeTitle = title;
            if (isITEInternal) {
              const workflowVersion = workflowVersions[step.workflowVersionId];
              const { 'workflow-name': workflowName } = workflowVersion.attributes;
              actionsNodeTitle = workflowName ?? title;
            }
            node = (
              <ActionsNode
                title={actionsNodeTitle}
                workflowVersionId={step.workflowVersionId}
                workflowStepToken={step.workflowStep.token}
                workflowSteps={
                  workflowVersions[step.workflowVersionId]?.attributes?.['steps-config']
                }
                hasError={workflowErrors.length > 0}
                selected={isActiveNode}
                isPlaceholder={
                  step.workflowStep.type === WorkflowStepType.Placeholder ||
                  step.workflowVersionId.includes('placeholder')
                }
                editable={editableForBasicTier}
                onActionsDoubleClick={handleNavToWorkflow}
              />
            );
            break;
          }
          case WorkflowStepType.Conditional: {
            node = (
              <ConditionalRootNode hasError={workflowErrors.length > 0} selected={isActiveNode} />
            );
            break;
          }
          default:
            return <></>;
        }
      } else {
        if (step.isConnector) {
          return (
            <div ref={(ref) => saveRef(ref, step, uniqueId)}>
              <WrappedConnectorNode
                dataName={dataName}
                dataTest={dataTest}
                dataMarketingId={dataMarketingId}
                connectingStep={step}
                theme={theme}
                editable={editableForBasicTier}
                handleNodeClick={() => selectNode(step.token)}
                handleConnectingStepChange={(stepName: string) => {
                  selectNode(stepName);
                  handleStepTypeSelected(ScreenFlowStepType.Connector, {
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    prevStep: previousStep,
                    branchIndex: previousStepBranchIndex,
                    nextStep: step,
                    connectingStepName: stepName,
                  });
                }}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                handleDeleteClick={
                  // if handleDeleteStep is not passed, pass null to hide edit button
                  deleteEnabled &&
                  (() =>
                    handleDeleteStep({
                      prevStep: previousStep,
                      branchIndex: previousStepBranchIndex,
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      nextStep: null,
                      currentStep: step,
                    }))
                }
                selected={isActiveNode}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                steps={steps.filter((step) => previousStep.token !== step.name)}
                title={title}
                screenDimensions={screenDimensions}
              />
            </div>
          );
        } else {
          stepStatuses = step.config.statuses.map((status) => status.name as InquiryStatus);
          switch (step.type) {
            case UiStepType.Complete: {
              node = <CompleteStepNode selected={isActiveNode} />;
              break;
            }
            case UiStepType.Document:
            case UiStepType.GovernmentId:
            case UiStepType.Selfie:
            case UiStepType.SelfieReflection:
            case UiStepType.VerifyWithPersona: {
              hoverEditLink = `${basePath}/screens/${step.name}`;
              node = (
                <InquiryTemplateScreenPreview
                  page={step.page}
                  step={step.stepPreview}
                  theme={theme}
                  onPreviewDoubleClick={handleNavToScreen}
                  screenDimensions={screenDimensions}
                />
              );
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              expandNodes = step.combinedStepPages.map((page) => (
                <InquiryTemplateScreenPreview
                  key={page.key}
                  page={page}
                  step={step.stepPreview}
                  theme={theme}
                  onPreviewDoubleClick={handleNavToScreen}
                  screenDimensions={screenDimensions}
                />
              ));
              break;
            }
            case UiStepType.Integration:
            case UiStepType.Ui: {
              hoverEditLink = `${basePath}/screens/${step.name}`;
              node = (
                <InquiryTemplateScreenPreview
                  page={step.page}
                  step={step.stepPreview}
                  theme={theme}
                  selected={isActiveNode}
                  onPreviewDoubleClick={handleNavToScreen}
                  screenDimensions={screenDimensions}
                />
              );
              break;
            }
            case UiStepType.Loop: {
              node = (
                <InquiryTemplateScreenPreview
                  page={step.page}
                  step={step.stepPreview}
                  theme={theme}
                  onPreviewDoubleClick={() => {}}
                  screenDimensions={screenDimensions}
                  tooltipText='Editing loop steps is not yet supported. Please contact someone at Persona to configure this template.'
                />
              );
              expandNodes = [
                <InquiryTemplateScreenPreview
                  page={step.page}
                  step={step.stepPreview}
                  theme={theme}
                  onPreviewDoubleClick={() => {}}
                  screenDimensions={screenDimensions}
                  tooltipText='Editing loop steps is not yet supported. Please contact someone at Persona to configure this template.'
                />,
              ];
              break;
            }
          }
        }
      }

      // complete and loop steps don't have editing UI
      let withHoverControls = step.type !== UiStepType.Complete && step.type !== UiStepType.Loop;
      // maintain static name sticker size for screen step previews
      const withStickerPortal = isScreenStep(step) && !step.isConnector;
      let ref = (ref: HTMLDivElement) => saveRef(ref, step, uniqueId);
      let withSticker = true;

      if (step.type === 'workflow' && step.workflowStep.type === WorkflowStepType.Conditional) {
        // save refs only for the conditional route nodes, omit the root node
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ref = null;
        withHoverControls = editableForBasicTier;
        withSticker = false;
      }

      const handleNodeClick = () => selectNode(step.token);
      const handleDeleteClick = deleteEnabled
        ? () => {
            handleDeleteStep({
              prevStep: previousStep,
              branchIndex: previousStepBranchIndex,
              nextStep,
              currentStep: step,
            });
          }
        : null;
      const handleCopyClick =
        enableCopyPaste && isScreenStep(step) ? () => handleCopyStep(step) : null;

      // Custom sticker label for loop steps
      const stickerLabel = step.type === UiStepType.Loop ? 'Loop Step' : undefined;

      const commonScreenWrapperProps = {
        ref,
        node,
        title,
        'data-name': dataName,
        'data-test': dataTest,
        'data-marketing-id': dataMarketingId,
        hoverEditLink,
        editable: editableForBasicTier,
        selected: isActiveNode,
        handleNodeClick,
        handleDeleteClick,
        handleCopyClick,
        withHoverControls,
        withStickerPortal,
        withSticker,
        stepStatuses,
        stickerLabel: stickerLabel,
      };

      return expandNodes.length > 0 ? (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <ExpandableScreenFlowNodeWrapper
          {...commonScreenWrapperProps}
          expandNodes={expandNodes}
          screenDimensions={screenDimensions}
        />
      ) : (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <ScreenFlowNodeWrapper {...commonScreenWrapperProps} />
      );
    },
    [
      activeNodeToken,
      basePath,
      editableForBasicTier,
      enableCopyPaste,
      isITEInternal,
      refMap,
      steps,
      theme,
      workflowValidationErrors,
      workflowVersions,
      handleCopyStep,
      handleDeleteStep,
      handleNavToScreen,
      handleNavToWorkflow,
      handleStepTypeSelected,
      saveRef,
      selectNode,
    ],
  );

  const renderConnector = useCallback(
    (prevStep: CombinedStepForTree, nextStep: CombinedStepForTree | undefined) => {
      if (!editableForBasicTier && nextStep == null) {
        // hide trailing connectors when user does not have tree editing permissions
        return <></>;
      }
      return (
        <ScreenFlowAddStepButton
          prevStep={prevStep}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          nextStep={nextStep}
          steps={steps}
          limitAddAction={limitAddAction}
          limitAddStep={limitAddStep}
          limitAddConditional={limitAddConditional}
          height={screenHeightWithPadding}
          screenDimensions={screenDimensions}
          editable={editableForFreeTier}
          hasAccessForBasic={hasAccessForBasicTier}
          hasVerificationWritePermission={hasVerificationWritePermission}
          handleStepTypeSelected={handleStepTypeSelected}
          clipboardHasStep={clipboardHasStep}
        />
      );
    },
    [
      steps,
      clipboardHasStep,
      editableForBasicTier,
      editableForFreeTier,
      hasAccessForBasicTier,
      hasVerificationWritePermission,
      limitAddAction,
      limitAddStep,
      limitAddConditional,
      handleStepTypeSelected,
    ],
  );

  const renderBranchRootConnector = useCallback(
    (rootStep: CombinedStepForTree, branchIndex: number) => {
      const isActiveNode = activeNodeToken === rootStep.token;
      const branchNodeToken = `${rootStep.token}.${branchIndex}`;
      const branchNodeRef: BranchNode = { type: 'branch', token: branchNodeToken };
      if (
        rootStep.type === 'workflow' &&
        rootStep.workflowStep.type === WorkflowStepType.Conditional
      ) {
        const workflowStep = getConditionalStepForToken(
          rootStep.workflowStep.token,
          workflowVersions[rootStep.workflowVersionId],
        );
        const routeIndex = branchIndex + 1;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const title = workflowStep.conditionals[branchIndex]?.name ?? `Route ${routeIndex}`;
        const nextStep =
          rootStep.branches?.length && rootStep.branches[branchIndex]?.length
            ? rootStep.branches[branchIndex][0]
            : null;
        return (
          <ConditionalWrapper>
            <ScreenFlowNodeWrapper
              ref={(ref) => saveRef(ref, branchNodeRef, branchNodeToken)}
              node={
                <ConditionalNode
                  title={title}
                  selected={isActiveNode}
                  hasError={
                    validationErrorsForWorkflowNode(
                      rootStep,
                      workflowVersions[rootStep.workflowVersionId].attributes['steps-config'],
                      workflowValidationErrors[rootStep.workflowVersionId] ?? [],
                      branchIndex,
                    ).length > 0
                  }
                />
              }
              title={title}
              data-name={kebabCase(title)}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              data-test={`${workflowStep.token}__${workflowStep.type}-branch-${branchIndex}-node`}
              data-marketing-id='ite-branch-tree-card'
              editable={editableForBasicTier}
              withHoverControls={editableForBasicTier}
              handleNodeClick={() => {
                selectNode(rootStep.token);
                selectConditionalRouteToEdit(routeIndex);
              }}
            />
            <ScreenFlowAddStepButton
              prevStep={rootStep}
              nextStep={nextStep}
              steps={steps}
              branchIndex={branchIndex}
              limitAddAction={limitAddAction}
              limitAddStep={limitAddStep}
              limitAddConditional={limitAddConditional}
              height={screenHeightWithPadding}
              screenDimensions={screenDimensions}
              editable={editableForFreeTier}
              hasAccessForBasic={hasAccessForBasicTier}
              hasVerificationWritePermission={hasVerificationWritePermission}
              handleStepTypeSelected={handleStepTypeSelected}
              clipboardHasStep={clipboardHasStep}
            />
          </ConditionalWrapper>
        );
      } else {
        let title = '';
        let description = null;
        let node: ReactNode = null;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const nextStep: CombinedStepForTree =
          rootStep.branches?.length && rootStep.branches[branchIndex]?.length
            ? rootStep.branches[branchIndex][0]
            : null;
        switch (rootStep.type) {
          case UiStepType.Document:
          case UiStepType.GovernmentId:
          case UiStepType.Selfie: {
            if (branchIndex === 0) {
              if (nextStep?.type === 'workflow') {
                title = nextStep.workflowStep.name;
              }
            } else {
              title = workflowErrorRouteLabel;
              description = workflowErrorRouteDescription;
            }
            node = (
              <ScreenTransitionNode
                title={title}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                description={description}
                selected={isActiveNode}
              />
            );
            break;
          }
          case UiStepType.Ui: {
            const transitionComponent = uiStepAllComponents(rootStep.stepPreview as StepUi).filter(
              isUiTransitionComponent,
            )?.[branchIndex];
            const title = isTextComponent(transitionComponent)
              ? transitionComponent.attributes.text
              : `Route ${branchIndex + 1}`;
            node = <ScreenTransitionNode title={title} selected={isActiveNode} />;
            break;
          }
          default:
            return <></>;
        }

        return (
          <ConditionalWrapper>
            <ScreenFlowNodeWrapper
              ref={(ref) => saveRef(ref, branchNodeRef, branchNodeToken)}
              node={node}
              title={title}
              data-name={kebabCase(title)}
              data-test={`${rootStep.token}__${rootStep.type}-branch-${branchIndex}-screen-step-node`}
              data-marketing-id='ite-branch-tree-card'
              editable={editableForBasicTier}
              handleNodeClick={noop}
            />
            <ScreenFlowAddStepButton
              prevStep={rootStep}
              nextStep={nextStep}
              steps={steps}
              branchIndex={branchIndex}
              limitAddAction={limitAddAction}
              limitAddStep={limitAddStep}
              limitAddConditional={limitAddConditional}
              height={screenHeightWithPadding}
              screenDimensions={screenDimensions}
              clipboardHasStep={clipboardHasStep}
              editable={editableForFreeTier}
              hasAccessForBasic={hasAccessForBasicTier}
              hasVerificationWritePermission={hasVerificationWritePermission}
              handleStepTypeSelected={handleStepTypeSelected}
            />
          </ConditionalWrapper>
        );
      }
    },
    [
      activeNodeToken,
      steps,
      workflowValidationErrors,
      workflowVersions,
      saveRef,
      clipboardHasStep,
      editableForBasicTier,
      editableForFreeTier,
      hasAccessForBasicTier,
      hasVerificationWritePermission,
      limitAddAction,
      limitAddStep,
      limitAddConditional,
      handleStepTypeSelected,
      selectConditionalRouteToEdit,
      selectNode,
    ],
  );

  // Used to style ScreenFlowBranchConnectors when branch node is selected
  const dashboardTheme = useContext(ThemeContext);
  const selectedBranchColor = dashboardTheme.color.primary.brand600;
  const pathHighlightInformationForStep = useCallback(
    (currentStep: CombinedStepForTree): PathHighlightInformation =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      currentStep.token === activeNodeToken
        ? {
            branchColor: selectedBranchColor,
          }
        : null,
    [activeNodeToken, selectedBranchColor],
  );

  return (
    <ScreenFlow
      innerRef={zoomableRef}
      steps={tree}
      stepContentHeight={screenDimensions.height}
      stepContentWidth={screenDimensions.width}
      pathHighlightInformationForStep={pathHighlightInformationForStep}
      renderStep={renderCard}
      renderConnector={renderConnector}
      renderBranchRootConnector={renderBranchRootConnector}
    />
  );
};

export default InquiryTemplateTree;
